/**
 * IMPORTANT! Do not use any services/composables here! (other than the ones defined in the context).
 *
 * This redirect handler should be used solely to determine whether user should be redirected
 * to a certain place or not, based on the application state, which is passed into the exported
 * function as first argument.
 *
 * Only imports that help to determine the redirect are allowed here (like consts or moment).
 */

import {
  BUSINESS_STATUSES,
  PAYMENT_SOURCES,
  BUSINESS_BOOST_STATUSES,
  MIGRATION_SOURCES,
} from '@/lib/consts.js'

import { replaceWithResolvedRoute } from '../utils/replace-with-resolved-route'

import type { RedirectContext } from '../types'

export const paymentMethodInfoRedirect = async ({ store, router, route, logger, emitter, flags }: RedirectContext): Promise<unknown> => {
  const overdueDetails = store.state.subscription.overduePayments.details
  const totalOverdue = store.state.subscription.overduePayments.total
  const businessStatus = store.getters['business/businessStatus']
  const boostStatus = store.state.business.business.boost_status

  const migratedFromVersum = store.state.business.business.migrated_from === MIGRATION_SOURCES.VERSUM
  const isMigrationCompleted = store.state.business.business?.is_migration_completed
  const isBoostOverdue = Number(totalOverdue?.boost_overdue_amount) > 0
  const isPathExcluded = ['/auth-me'].some((path) => route?.params?.redirection?.path?.includes(path))

  if (isPathExcluded) return

  if (store.getters['identity/isManagerOrHigher']) {
    let paymentMethodMessage = ''

    const paymentSource = store.getters['business/paymentSource']
    if (paymentSource === PAYMENT_SOURCES.BOOKSY_BILLING) {
    // billingSettings is set only if access level is manager or higher
      if (businessStatus === BUSINESS_STATUSES.TRIAL_BLOCKED) {
        paymentMethodMessage = 'trial-blocked'
      }
      if (businessStatus === BUSINESS_STATUSES.PAID_ACTIVE
         && isBoostOverdue
      ) {
        if (boostStatus === BUSINESS_BOOST_STATUSES.DISABLED) {
          paymentMethodMessage = 'boost-blocked'
        } else {
          paymentMethodMessage = 'pay-overdue-boost-fees'
        }
      }
      if (businessStatus === BUSINESS_STATUSES.ACTIVE_OVERDUE) {
        if (migratedFromVersum && !isMigrationCompleted) {
          return replaceWithResolvedRoute(router, {
            name: 'calendar',
            params: {
              businessId: store.getters['business/businessId'],
            },
          })
        }
        if (isBoostOverdue && Number(totalOverdue?.subscription_overdue_amount) > 0) {
          paymentMethodMessage = 'pay-overdue-fees'
        } else {
          try {
            if (overdueDetails?.account_lock_in_days === 1) {
            // "your account will be blocked tomorrow"
              paymentMethodMessage = 'warning'
            } else {
            // "your account will be blocked soon"
              paymentMethodMessage = 'failed'
            }
            emitter.emit(emitter.EVENTS.ANALYTICS_DISPATCH, emitter.EVENTS.SUBSCRIPTION_TRIAL_WILL_EXPIRE_SHOWN)
          } catch (e) {
            logger.error('overdueDetails', e)
          }
        }
      }

      if (businessStatus === BUSINESS_STATUSES.BLOCKED_OVERDUE) {
      // "your account is blocked"
        paymentMethodMessage = 'blocked'
        emitter.emit(emitter.EVENTS.ANALYTICS_DISPATCH, emitter.EVENTS.SUBSCRIPTION_TRIAL_HAS_EXPIRED_SHOWN)
      }

      if (businessStatus === BUSINESS_STATUSES.CHURNED) {
        paymentMethodMessage = 'churned'
      }
    } else if (paymentSource === PAYMENT_SOURCES.OFFLINE) {
      if (businessStatus === BUSINESS_STATUSES.TRIAL_BLOCKED) {
        paymentMethodMessage = 'trial-blocked'
        emitter.emit(emitter.EVENTS.ANALYTICS_DISPATCH, emitter.EVENTS.SUBSCRIPTION_TRIAL_HAS_EXPIRED_SHOWN)
      } else if ([BUSINESS_STATUSES.BLOCKED_OVERDUE, BUSINESS_STATUSES.CHURNED]
        .includes(businessStatus)) {
        paymentMethodMessage = 'overdue-blocked'
      }
    } else if (paymentSource === PAYMENT_SOURCES.UNKNOWN) {
      if (businessStatus === BUSINESS_STATUSES.TRIAL_BLOCKED) {
        paymentMethodMessage = 'trial-blocked'
      }
    }

    if (paymentMethodMessage) {
      logger.debug(`Business status: ${businessStatus} >>> payment/info/${paymentMethodMessage}`)

      const params: Record<string, any> = {
        businessId: store.getters['business/businessId'],
        type: paymentMethodMessage,
      }
      if (route?.params?.redirection?.path) {
        const returnPath = [
          `${route?.params?.redirection?.path}`,
          new URLSearchParams(route?.params?.redirection?.query).toString(),
        ].join('?')
        params.returnPath = returnPath
      }
      return replaceWithResolvedRoute(router, {
        name: 'settings.subscription.payment-method-info',
        params,
      })
    }
  } else if (businessStatus === BUSINESS_STATUSES.BLOCKED_OVERDUE) {
    logger.debug(`Business status: ${businessStatus} >>> staffer-blocked-info`)
    return replaceWithResolvedRoute(router, {
      name: 'staffer-blocked-info',
      params: {
        businessId: store.getters['business/businessId'],
      },
    })
  }
}
